/**
 * Frido Size Pills runtime — the pills/cards grid for the option axis named
 * by the placement's `axis` prop. One DOM skeleton; optional adornment rows
 * are all props: gender_axis (Men/Women switch), caption
 * (drawer link). The Inches/Feet/CM unit toggle auto-shows for dimension
 * sizes (data-driven, no prop). `layout` prop picks the label converter
 * (pills | dimensions | uk | auto value-format resolution) — same grid either
 * way. The kids radio TABLE is fszfrd02, a different component.
 *
 * Picking a value selects the matching variant (?variant= + event).
 */
;(() => {
  if (window.__sai_fszfrd01_initialized__) return
  window.__sai_fszfrd01_initialized__ = true

  const SNIPPET_ID = 'fszfrd01'
  const TAG = 'sai-fszfrd01'
  const FEATURE_SLUG = 'variant_picker'

  function esc(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
  function arrowSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg>'
  }
  function rulerSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h18v8H3z"/><path d="M7 8v3M11 8v4M15 8v3M19 8v4"/></svg>'
  }
  function readPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i]
      if (c.tagName === 'SCRIPT' && c.hasAttribute('data-spectrum-snippet-pool')) {
        try { return JSON.parse(c.textContent) } catch (e) { return {} }
      }
    }
    return {}
  }

  const DIM_RE = /^\s*\d+(?:\.\d+)?\s*(?:["”]|\s*(?:in|inch|inches))?\s*[xX×]\s*\d+(?:\.\d+)?\s*(?:["”]|\s*(?:in|inch|inches))?\s*$/
  const UK_RE = /\bUK\b/i

  function detectLayout(values, forced) {
    if (forced && forced !== 'auto') return forced
    if (values.length && values.every((v) => DIM_RE.test(v))) return 'dimensions'
    if (values.length && values.every((v) => UK_RE.test(v))) return 'uk'
    return 'pills'
  }

  // "72\" x 36\"" -> converted dimension label per unit
  function convertDim(label, unit) {
    if (unit === 'inch') return label
    const nums = String(label).match(/\d+(?:\.\d+)?/g)
    if (!nums || !nums.length) return label
    const factor = unit === 'feet' ? 1 / 12 : 2.54
    const sym = unit === 'feet' ? 'ft' : 'cm'
    const conv = nums.map((n) => (Math.round(Number.parseFloat(n) * factor * 10) / 10).toString())
    return conv.join(' x ') + ' ' + sym
  }

  // "9 UK" -> foot-length via barleycorn approximation ((size + 23) / 3 inches)
  function convertUk(label, unit) {
    if (unit === 'inch') return label
    const m = String(label).match(/(\d+(?:\.\d+)?)/)
    if (!m) return label
    const inches = (Number.parseFloat(m[1]) + 23) / 3
    if (unit === 'feet') return (Math.round((inches / 12) * 100) / 100) + ' ft'
    return (Math.round(inches * 2.54 * 10) / 10) + ' cm'
  }

  function optKey(opt) { return 'option' + (opt ? opt.position : 1) }
  function variantsFor(pool, selections) {
    return (pool.product.variants || []).filter((v) =>
      Object.entries(selections).every(([k, val]) => !val || v[k] === val))
  }
  function isAvailable(pool, selections) { return variantsFor(pool, selections).some((v) => v.available) }
  function pickVariant(pool, selections, cur, freeKeys) {
    const vs = variantsFor(pool, selections)
    // Axes below this one move only when they must: keep the shopper's existing
    // choice while it is still sellable, otherwise advance to something that is,
    // so a pick can never land on a dead combination the picker showed as fine.
    const keeps = (v) => !cur || !freeKeys || freeKeys.every((k) => cur[k] == null || v[k] === cur[k])
    return vs.find((v) => v.available && keeps(v)) || vs.find((v) => v.available)
      || vs.find(keeps) || vs[0] || null
  }

  // ---- Top-down axis cascade ------------------------------------------------
  // A picker judges availability against the axes ABOVE it on the page, never
  // the ones below. The first picker a shopper reads therefore shows every
  // value it can still sell in some combination, and each picker beneath it
  // narrows to the choice already made. Which axis is "on top" is read from the
  // DOM, so re-ordering the blocks in a template flips the cascade with no code
  // change and the two axes can never disagree about what is in stock.
  //
  // An axis with no picker of its own is always pinned — nothing on the page
  // can change it, so it constrains everything.
  //
  // Pickers are grouped by their nearest <dialog> ancestor so the mini-PDP's
  // copy of a picker never constrains the one in the buybox.
  const AXIS_PICKERS = 'sai-fszfrd01[data-sai-axis],sai-fswfrd01[data-sai-axis]'
  function axisGroup(el) { return el.closest('dialog') || document }
  function axisKeysBelow(el) {
    const group = axisGroup(el)
    const keys = []
    for (const p of group.querySelectorAll(AXIS_PICKERS)) {
      if (p === el || axisGroup(p) !== group) continue
      if (!(el.compareDocumentPosition(p) & Node.DOCUMENT_POSITION_FOLLOWING)) continue
      keys.push(p.getAttribute('data-sai-axis'))
    }
    return keys
  }



  const El = class extends HTMLElement {
    connectedCallback() {
      if (this._init) return
      this._init = true
      this._track = () => {}
      // Another axis snippet (e.g. Category pills) selecting a variant must
      // re-filter our size values — listen for the shared variant-change event,
      // adopt the new variant's other-axis options, and re-render. Our own
      // picks set _curVariantId first (see _applyVariantSelection) so this is a
      // no-op for events we fired.
      this._onVariantChange = (e) => {
        const id = e && e.detail && e.detail.variantId
        if (id == null || String(id) === String(this._curVariantId) || !this._pool) return
        this._curVariantId = id
        const nv = (this._pool.product.variants || []).find((v) => String(v.id) === String(id))
        if (nv) {
          const sk = optKey(this._pool.product.size_option)
          if (nv[sk] != null) this._selected = nv[sk]
        }
        this._render()
      }
      window.addEventListener('spectrum:variant-change', this._onVariantChange)
    }
    disconnectedCallback() {
      if (this._onVariantChange) window.removeEventListener('spectrum:variant-change', this._onVariantChange)
    }
    setAnalytics(t) { this._track = typeof t === 'function' ? t : () => {} }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      const p = this._pool.product
      if (!p || !p.size_option) return
      const props = this._pool.props || {}
      this._layout = detectLayout(p.size_option.values, props.layout)
      if (!this._unit) this._unit = props.default_unit || 'inch'
      this._selected = p.size_option.selected || null
      this._gender = p.gender_option ? (p.gender_option.selected || p.gender_option.values[0]) : null
      this._render()
      this._bind()
    }

    _props() { return (this._pool && this._pool.props) || {} }


    _showToggle() {
      // Dimension sizes (e.g. 72" x 36") are the one size set that reads as a
      // unit, so the inline Inches/Feet/CM switch shows only for them — where
      // its convertDim output is meaningful. UK / pills sizes are not
      // convertible, so the toggle stays hidden (matching live's single-unit
      // render). Purely data-driven off the resolved layout: one instance on a
      // shared template self-gates per product, no config prop involved.
      return this._layout === 'dimensions'
    }

    // Dimension cards lay out automatically: 2 per row on mobile, 3 on desktop.
    // Verified across the whole Frido catalogue — short shoe sizes, UK bands,
    // and dimension cards all read best this way, so it's fixed (no merchant
    // knob). Short-label values use the pills layout (hug-and-wrap) instead.
    _colsD() { return '3' }
    _cols() { return '2' }

    _displayLabel(v) {
      if (this._layout === 'dimensions') return convertDim(v, this._unit)
      if (this._layout === 'uk') return convertUk(v, this._unit)
      return v
    }

    // Split the label into the variant-attribute NAME and its "(unit conversion)"
    // suffix (e.g. "6 UK (40 EU)" → "6 UK" + "(40 EU)"), each in its own span, so
    // the OOS grey/strikethrough can be applied to the name only — not the unit.
    _labelHtml(label) {
      const s = String(label)
      const i = s.indexOf(' (')
      if (i > -1 && s.endsWith(')')) {
        return `<span class="sai-fszfrd01__nm">${esc(s.slice(0, i))}</span><span class="sai-fszfrd01__uc">${esc(s.slice(i))}</span>`
      }
      return `<span class="sai-fszfrd01__nm">${esc(s)}</span>`
    }

    // Axes owned by a picker rendered below this one. They stay unpinned so this
    // picker reports everything it can still sell (see the cascade note above).
    _freeKeys() { return axisKeysBelow(this) }

    _selections(value) {
      const prod = this._pool.product
      const sizeKey = optKey(prod.size_option)
      const genderKey = prod.gender_option ? optKey(prod.gender_option) : null
      const sel = { [sizeKey]: value }
      // Pin the OTHER axes (type / colour / category) that sit above this picker
      // so an availability check reflects the real combination: a size can
      // co-exist with the selected type yet be out of stock for it, and must
      // strike. Same constraint set _visibleValues uses to decide which sizes
      // co-exist. The gender switch lives inside this picker, above the size
      // values, so it always pins.
      const free = this._freeKeys()
      const cur = this._currentVariant()
      if (cur) {
        for (const k of ['option1', 'option2', 'option3']) {
          if (k === sizeKey || k === genderKey) continue
          if (free.indexOf(k) !== -1) continue
          if (cur[k] != null) sel[k] = cur[k]
        }
      }
      if (genderKey && this._gender) sel[genderKey] = this._gender
      return sel
    }

    _currentVariant() {
      const id = this._curVariantId != null ? this._curVariantId : this._pool.product.current_variant_id
      return (this._pool.product.variants || []).find((v) => String(v.id) === String(id)) || null
    }

    // Only show size values that co-exist with the current selection of the OTHER
    // axes (e.g. a mattress topper's Category — a Single topper has different
    // sizes than a Queen). Live filters sizes to the ones valid for the selected
    // category. A product with no other axis is unaffected: no constraints ->
    // every size shown, identical to the previous behaviour.
    _visibleValues() {
      const prod = this._pool.product
      const opt = prod.size_option
      const sizeKey = optKey(opt)
      const genderKey = prod.gender_option ? optKey(prod.gender_option) : null
      const cur = this._currentVariant()
      const free = this._freeKeys()
      const constraints = {}
      if (cur) {
        for (const k of ['option1', 'option2', 'option3']) {
          if (k === sizeKey || k === genderKey) continue
          if (free.indexOf(k) !== -1) continue
          if (cur[k] != null) constraints[k] = cur[k]
        }
      }
      if (genderKey && this._gender) constraints[genderKey] = this._gender
      const keys = Object.keys(constraints)
      if (!keys.length) return opt.values
      return opt.values.filter((v) =>
        (prod.variants || []).some((variant) => variant[sizeKey] === v && keys.every((k) => variant[k] === constraints[k])))
    }

    _render() {
      const p = this._props()
      const prod = this._pool.product
      const opt = prod.size_option
      const label = `<div class="sai-fszfrd01__label"><span class="sai-fszfrd01__label-key">${esc(p.label_prefix || 'SIZE :')}</span><span class="sai-fszfrd01__label-val" data-sel-label>${esc(this._selected || '')}</span></div>`

      // units and the gender switch are independent: units need convertible
      // values, the switch only needs a 2-value gender axis on the product
      const showUnits = this._showToggle()
      const showGender = !!(prod.gender_option && prod.gender_option.values.length === 2)
      let units = ''
      if (showUnits) {
        units = '<div class="sai-fszfrd01__units">'
        for (const [u, lbl] of [['inch', 'Inches'], ['feet', 'Feet'], ['cm', 'CM']]) {
          units += `<button type="button" class="sai-fszfrd01__unit" data-action="unit" data-unit="${u}" data-active="${u === this._unit}">${lbl}</button>`
        }
        units += '</div>'
      }
      let gender = ''
      if (showGender) {
        const [a, b] = prod.gender_option.values
        const pos = this._gender === b ? 'right' : 'left'
        gender = `<div class="sai-fszfrd01__gender">
          <span class="sai-fszfrd01__gender-lbl" data-action="gender" data-value="${esc(a)}" data-active="${this._gender === a}">${esc(a)}</span>
          <button type="button" class="sai-fszfrd01__switch" data-action="gender-switch" data-pos="${pos}" aria-label="Toggle ${esc(prod.gender_option.name)}"></button>
          <span class="sai-fszfrd01__gender-lbl" data-action="gender" data-value="${esc(b)}" data-active="${this._gender === b}">${esc(b)}</span>
        </div>`
      }

      const values = this._visibleValues()
      // oos_display prop (Studio): 'strike' (default — struck + selectable) | 'hide' (drop sold-out
      // values) | 'disable' (show greyed, not clickable). The liquid _body SSR capture MUST mirror
      // this branch or first paint and hydration diverge.
      const oosMode = p.oos_display || 'strike'
      const sizeBtn = (v, cls0, label) => {
        const avail = isAvailable(this._pool, this._selections(v))
        if (!avail && oosMode === 'hide') return ''
        const disabled = !avail && oosMode === 'disable'
        const cls = [cls0, v === this._selected ? 'is-sel' : '', avail ? '' : 'is-oos', disabled ? 'is-disabled' : ''].filter(Boolean).join(' ')
        return `<button type="button" class="${cls}"${disabled ? ' aria-disabled="true"' : ' data-action="pick"'} data-value="${esc(v)}">${label}</button>`
      }
      let body = ''
      if (this._layout === 'pills') {
        body = '<div class="sai-fszfrd01__pills">'
        for (const v of values) body += sizeBtn(v, 'sai-fszfrd01__pill', this._labelHtml(v))
        body += '</div>'
      } else {
        body = `<div class="sai-fszfrd01__grid" data-cols="${this._cols()}" data-cols-d="${this._colsD()}">`
        for (const v of values) body += sizeBtn(v, 'sai-fszfrd01__card', this._labelHtml(this._displayLabel(v)))
        body += '</div>'
      }


      // Header layout follows the mock and depends on which toggles apply:
      //  - unit toggle present: the SIZE label is its own row, then a toggle row
      //    (units left, gender right) with a hairline under it — the label needs
      //    its own line because the units already fill the row's left side.
      //  - no units but a gender switch: the label and the gender switch share
      //    one row (label left, gender right), hairline under it.
      //  - neither: just the label, no divider, so the grid flows straight up.
      // Inline styles (not {% stylesheet %}) so a layout change isn't held back
      // by the theme's compiled-CSS cache. MUST match the SSR _head in the liquid.
      const barStyle = 'display:flex;align-items:center;justify-content:space-between;gap:16px;border-bottom:0.5px solid var(--f-border);padding-bottom:8px'
      let head
      if (showUnits) {
        head = `${label}<div class="sai-fszfrd01__toggles" style="${barStyle}">${units}${gender}</div>`
      } else if (showGender) {
        head = `<div class="sai-fszfrd01__toggles" style="${barStyle}">${label}${gender}</div>`
      } else {
        head = label
      }
      const caption = p.caption ? `<div class="sai-fszfrd01__caption">${esc(p.caption)}</div>` : ''
      this.innerHTML = `<div class="sai-fszfrd01__inner">${head}${body}${caption}</div>`
    }

    _bind() {
      if (this._clickBound) return
      this._clickBound = true
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-action]')
        if (!t) return
        const a = t.dataset.action
        if (a === 'unit') return this._setUnit(t.dataset.unit)
        if (a === 'pick') return this._pick(t.dataset.value)
        if (a === 'gender') return this._setGender(t.dataset.value)
        if (a === 'gender-switch') return this._toggleGender()
      })
    }

    _setUnit(unit) {
      if (!unit || unit === this._unit) return
      this._unit = unit
      this._render()
      this._track(`${FEATURE_SLUG}:unit_toggle_elem_clicked`, { unit })
    }

    _setGender(value) {
      if (!value || value === this._gender) return
      // The two genders can expose different size-value strings (e.g. the UK
      // range differs: "XS(3-6 UK)" vs "XS(4-5 UK)"), so the current selection has
      // no counterpart in the new gender's set — every pill would render
      // unselected and the label would stay stale. Reconcile to the same-rank size
      // (same position in the visible list), falling back to the first.
      const oldVisible = this._visibleValues()
      const oldIdx = this._selected ? oldVisible.indexOf(this._selected) : -1
      this._gender = value
      const newVisible = this._visibleValues()
      if (newVisible.length) {
        this._selected = newVisible[oldIdx >= 0 ? Math.min(oldIdx, newVisible.length - 1) : 0]
      }
      this._applyVariantSelection()
      this._render()
      this._track(`${FEATURE_SLUG}:gender_toggle_elem_clicked`, { value })
    }

    _toggleGender() {
      const vals = this._pool.product.gender_option.values
      this._setGender(this._gender === vals[0] ? vals[1] : vals[0])
    }

    _pick(value) {
      // OOS sizes stay selectable (matches live): picking one selects its
      // out-of-stock variant so the ATC shows OUT OF STOCK, rather than blocking
      // the tap and trapping the shopper.
      this._selected = value
      this._applyVariantSelection()
      this._render()
      this._track(`${FEATURE_SLUG}:select_variant`, { value })
    }

    _applyVariantSelection() {
      if (!this._selected) return
      const variant = pickVariant(
        this._pool, this._selections(this._selected), this._currentVariant(), this._freeKeys())
      if (variant) {
        // mark first so our own variant-change listener treats this as a no-op
        this._curVariantId = variant.id
        try {
          const url = new URL(window.location.href)
          url.searchParams.set('variant', String(variant.id))
          window.history.replaceState({}, '', url.toString())
        } catch (e) {}
        window.dispatchEvent(new CustomEvent('spectrum:variant-change', { detail: { variantId: variant.id } }))
        // Sync the theme product form's hidden variant input. This snippet
        // replaces the native variant picker, and product-form.js submits
        // whatever is in `[name="id"]` — without this, add-to-cart uses the
        // default variant regardless of the picked size. Scope to forms whose
        // current value is one of THIS product's variants so upsell forms for
        // other products are never touched.
        try {
          const _vids = {}
          ;(this._pool.product.variants || []).forEach((vv) => {
            _vids[String(vv.id)] = 1
          })
          document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
            if (_vids[String(input.value)]) {
              input.value = String(variant.id)
              input.dispatchEvent(new Event('change', { bubbles: true }))
            }
          })
        } catch (e) {}
      }
    }

  }

  if (!customElements.get(TAG)) customElements.define(TAG, El)

  function bindAll() {
    const api = window.__spectrumAi && window.__spectrumAi.snippet
    for (const node of document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
      const el = node.querySelector(TAG)
      if (!el) continue
      // First paint straight from the server-rendered pool — the pool's props
      // are the instance's default config, so rendering never waits on the
      // SDK (which may load after DOMContentLoaded or resolve asynchronously).
      if (!el._painted) {
        const initPool = readPool(node)
        if (initPool && (initPool.product || initPool.bundle || initPool.axis || initPool.buying_options)) { el._painted = true; el.applyVariant({}, initPool) }
      }
      if (api && typeof api.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = api.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (e) e.applyVariant(ctx.variant ? ctx.variant.content : {}, readPool(node))
        })
        if (handles) el.setAnalytics(handles.track)
      }
    }
    for (const el of document.querySelectorAll(TAG)) {
      if (el._applied || el._wrapBound || el.closest('[data-spectrum-instance-id]')) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readPool(parent)
      if (pool && pool.product) { el._applied = true; el.applyVariant({}, pool) }
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiFszfrd01 = { detectLayout, convertDim, convertUk }
  }

  function schedule() {
    bindAll()
    // the SDK can load after us — retry until it binds (or give up quietly)
    let tries = 0
    const t = setInterval(() => {
      bindAll()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true })
  } else {
    schedule()
  }

  // The storefront SDK can re-mount slot wrappers after load (conflict
  // resolution / vis flips) — a replaced wrapper holds a fresh unpainted
  // element, so rebind on DOM changes, debounced.
  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAll() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  // Injected-content rebinding survives the observer's time window (quick-add drawer).
  window.__saiRebind = window.__saiRebind || {}
  window.__saiRebind['fszfrd01'] = bindAll
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
